import { lazy, Suspense } from 'react'
import { createFileRoute, redirect, useSearch } from '@tanstack/react-router'

import { FormsSidebar, type FormsSection } from '@/components/settings/forms-sidebar'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { requirePermission, PermissionDeniedRoute } from '@/lib/require-permission-guard'

const EmbedFormSection = lazy(() =>
  import('@/components/settings/forms/embed-form-section').then((module) => ({
    default: module.EmbedFormSection,
  }))
)
const FormOptionsSection = lazy(() =>
  import('@/components/settings/forms/form-options-section').then((module) => ({
    default: module.FormOptionsSection,
  }))
)
const UnavailableDatesSection = lazy(() =>
  import('@/components/settings/forms/unavailable-dates-section').then((module) => ({
    default: module.UnavailableDatesSection,
  }))
)
const ThemeSection = lazy(() =>
  import('@/components/settings/forms/branding-section').then((module) => ({
    default: module.BrandingSection,
  }))
)
const CustomizationSection = lazy(() =>
  import('@/components/settings/forms/customization-section').then((module) => ({
    default: module.CustomizationSection,
  }))
)
const AnalyticsSection = lazy(() =>
  import('@/components/settings/forms/analytics-section').then((module) => ({
    default: module.AnalyticsSection,
  }))
)

type FormsSearch = {
  section?: FormsSection
}

const legacySectionMap: Record<string, FormsSection> = {
  embed: 'publish',
  options: 'fields-services',
  customization: 'confirmation',
  'forms-embed': 'publish',
  'forms-options': 'fields-services',
  'forms-customization': 'confirmation',
  branding: 'theme',
  'forms-branding': 'theme',
  'forms-analytics': 'analytics',
  'forms-unavailable-dates': 'availability',
}

export const Route = createFileRoute('/_app/_settings/forms')({
  component: FormsPage,
  validateSearch: (search: Record<string, unknown>): FormsSearch => {
    return {
      section: (search.section as FormsSection) || 'publish',
    }
  },
  beforeLoad: async ({ context, search }) => {
    const rawSection = search.section as string | undefined
    if (rawSection && legacySectionMap[rawSection]) {
      throw redirect({
        to: '/forms',
        search: { section: legacySectionMap[rawSection] },
      })
    }
    await requirePermission(context.queryClient, 'SettingsManage', 'inquiry form settings')
  },
  errorComponent: PermissionDeniedRoute,
})

function FormsPage() {
  useBreadcrumbFromRoute()
  const search = useSearch({ from: '/_app/_settings/forms' })
  const currentSection = search.section || 'publish'

  return (
    <div className="flex h-full overflow-hidden">
      <div className="scroll-fade-y flex-shrink-0 overflow-y-auto">
        <FormsSidebar />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-4 p-6">
          <Suspense fallback={<SectionLoadingSkeleton />}>
            {currentSection === 'publish' && <EmbedFormSection />}
            {currentSection === 'fields-services' && <FormOptionsSection />}
            {currentSection === 'availability' && <UnavailableDatesSection />}
            {currentSection === 'theme' && <ThemeSection />}
            {currentSection === 'confirmation' && <CustomizationSection />}
            {currentSection === 'analytics' && <AnalyticsSection />}
          </Suspense>
        </div>
      </div>
    </div>
  )
}

function SectionLoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div>
        <SkeletonBar className="mb-2 h-8 w-64" />
        <SkeletonBar className="h-4 w-96" />
      </div>
      <Card>
        <CardHeader>
          <SkeletonBar className="h-6 w-48" />
          <SkeletonBar className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-4">
          <SkeletonBar className="h-10 w-full" />
          <SkeletonBar className="h-10 w-full" />
          <SkeletonBar className="h-10 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}
